/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { cssMap, jsx } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';

import { token } from '@atlaskit/tokens';

import { metricSelectionMessages } from '../../../../utils/metricSelectionMessages';
import type { SelectedMetric } from '../../../../utils/orgMetrics';
import { SpacingWrapper } from '../../SpacingWrapper';

import { messages } from './messages';

const styles = cssMap({
	root: {
		maxWidth: '100%',
		// NO ADS token exists for 120px
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		paddingBottom: '120px',
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.400'),
		// NO ADS token exists for 88px
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		paddingInline: '88px',
		'@media (max-width: 30rem)': {
			paddingInline: token('space.0'),
		},
	},
	heading: {
		margin: 0,
		maxWidth: '1200px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontFamily: "'Charlie Display', 'Charlie Text', sans-serif",
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: 1.5,
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontWeight: 400,
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		letterSpacing: '0.5px',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: token('color.text.subtle'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontSize: '2rem',
		'@media (max-width: 48rem)': {
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
			fontSize: '32px',
		},
		'@media (max-width: 30rem)': {
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
			fontSize: '24px',
		},
	},
	grid: {
		display: 'grid',
		width: '100%',
		maxWidth: '1200px',
		gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
		gap: token('space.300'),
		'@media (max-width: 48rem)': {
			gridTemplateColumns: '1fr',
		},
	},
	card: {
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		backgroundColor: token('color.background.disabled'),
		borderRadius: token('radius.medium'),
		// NO ADS token exists for 360px
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		minHeight: '360px',
		// NO ADS token exists for 30px
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		padding: '30px',
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'space-between',
	},
	value: {
		margin: 0,
		color: token('color.text'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontFamily: token('font.family.code'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontSize: '80px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: 1,
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontWeight: 300,
	},
	label: {
		margin: 0,
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: token('color.text.subtle'),
		font: token('font.heading.small'),
		fontFamily: token('font.family.code'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontWeight: 600,
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		letterSpacing: '1px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		textTransform: 'uppercase',
	},
});

type ConnectedAppsHighlightSectionProps = {
	stats?: readonly SelectedMetric[];
};

export function ConnectedAppsHighlightSection({
	stats,
}: ConnectedAppsHighlightSectionProps): JSX.Element | null {
	const { formatNumber } = useIntl();

	if (!stats?.length) {
		return null;
	}

	return (
		<SpacingWrapper>
			<section css={styles.root}>
				{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
				<p css={styles.heading}>
					<FormattedMessage {...messages.heading} />
				</p>
				<div css={styles.grid}>
					{stats?.map((stat) => {
						const formattedValues = Object.fromEntries(
							Object.entries(stat.values).map(([key, value]) => [
								key,
								typeof value === 'number'
									? formatNumber(value, {
											notation: 'compact',
											maximumFractionDigits: 2,
										})
									: value,
							]),
						);
						return (
							<div key={stat.copyKey} css={styles.card}>
								{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
								<p css={styles.value}>
									{formatNumber(stat.value, {
										notation: 'standard',
										maximumFractionDigits: 0,
									})}
								</p>
								{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
								<p css={styles.label}>
									<FormattedMessage
										{...metricSelectionMessages[
											stat.copyKey as keyof typeof metricSelectionMessages
										]}
										values={formattedValues}
									/>
								</p>
							</div>
						);
					})}
				</div>
			</section>
		</SpacingWrapper>
	);
}
